import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash-es';
import { useOverlayContext } from 'seagull';
import { useIoContext } from '~/contexts';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useLocaleKeys } from '~/i18n';
import { useTasksJobs } from '../../hooks/useTasksJobs';
import { isTerminal } from '../../taskModel';
import { useTasksDrawer } from '../../tasksDrawerStore';
import { buildCompletionToastSpec, GetCompletionToastTID, pickTransitionData } from '../utils/completionToastSpec';
export const COMPLETION_TOAST_DEBOUNCE_MS = 3000;
export { GetCompletionToastTID };
export function useCompletionToast() {
    const { t: texts } = useLocaleKeys('tasksDrawer');
    const { showSuccessToast, showWarningToast } = useOverlayContext();
    const { browser: { openNewTab } } = useIoContext();
    const { retryAgreementPdf } = useDownloadAgreementPdf();
    const { tasks, isSettled } = useTasksJobs();
    const { isDrawerOpen, openDrawer } = useTasksDrawer();
    const bufferRef = useRef([]);
    const prevTasksRef = useRef(new Map());
    const initializedRef = useRef(false);
    const isDrawerOpenRef = useRef(isDrawerOpen);
    isDrawerOpenRef.current = isDrawerOpen;
    const emit = useCallback(()=>{
        const transitions = bufferRef.current;
        bufferRef.current = [];
        if (isDrawerOpenRef.current) return;
        const spec = buildCompletionToastSpec(transitions, texts, {
            openNewTab,
            openDrawer,
            retry: retryAgreementPdf
        });
        if (spec == null) return;
        if (spec.kind === 'success') showSuccessToast(spec.content, spec.testId);
        else showWarningToast(spec.content, spec.testId);
    }, [
        showSuccessToast,
        showWarningToast,
        openNewTab,
        retryAgreementPdf,
        texts,
        openDrawer
    ]);
    const emitRef = useRef(emit);
    useEffect(()=>{
        emitRef.current = emit;
    }, [
        emit
    ]);
    const debouncedEmit = useMemo(()=>debounce(()=>emitRef.current(), COMPLETION_TOAST_DEBOUNCE_MS, {
            leading: false,
            trailing: true
        }), []);
    useEffect(()=>{
        if (!isSettled) return;
        if (!initializedRef.current) {
            initializedRef.current = true;
            prevTasksRef.current = new Map(tasks.map((t)=>[
                    t.jobId,
                    t.status
                ]));
            return;
        }
        const prev = prevTasksRef.current;
        const next = new Map();
        const transitions = [];
        for (const task of tasks){
            next.set(task.jobId, task.status);
            const prevStatus = prev.get(task.jobId);
            if (task.cancelling) continue;
            if (!isTerminal(task.status)) continue;
            if (prevStatus != null && isTerminal(prevStatus)) continue;
            transitions.push(_object_spread({
                jobId: task.jobId,
                nextStatus: task.status
            }, pickTransitionData(task)));
        }
        prevTasksRef.current = next;
        if (isDrawerOpenRef.current) {
            bufferRef.current = [];
            debouncedEmit.cancel();
            return;
        }
        if (transitions.length === 0) return;
        bufferRef.current.push(...transitions);
        debouncedEmit();
    }, [
        tasks,
        isSettled,
        debouncedEmit
    ]);
    useEffect(()=>()=>debouncedEmit.cancel(), [
        debouncedEmit
    ]);
}
